import { useState } from 'react';
import { useVersionPolicy } from '@/hooks/useVersionPolicy';
import { markDismissed, recentlyDismissed } from '@/lib/updateDismiss';
import { SUPPORT_WHATSAPP_URL, storeUrlForNative } from '@/lib/storeLinks';
import {
  UpdateAppBanner,
  UpdateRequiredScreen
} from '@/components/app/UpdateAppScreens';

/**
 * Ligação entre a régua do backend e as duas telas. Molde de "trigger global"
 * do `LevelChangeToast`: busca no mount, `return null` se não há nada,
 * delega o desenho.
 *
 * Os dois níveis moram em lugares DIFERENTES de propósito:
 *  - `UpdateAppGate` monta no root (`App.tsx`), fora do `AppLayout` — o
 *    bloqueio duro precisa valer também no `/login`, e o `AppLayout` só
 *    envolve rotas autenticadas.
 *  - `UpdateAppBannerHost` monta no `AppLayout` — a faixa é um convite, não
 *    faz sentido antes de o cliente sequer entrar.
 *
 * Fallbacks de texto existem só como rede: o backend sempre preenche.
 */

const FALLBACK_HARD_TITLE = 'Atualize o Bow para continuar';
const FALLBACK_HARD_MESSAGE =
  'Esta versão do app é antiga demais. Atualize na loja para voltar a usar.';
const FALLBACK_SOFT_TITLE = 'Atualização disponível';
const FALLBACK_SOFT_MESSAGE = 'Saiu uma versão nova do Bow na loja.';
const FALLBACK_CTA = 'Atualizar';

export function UpdateAppGate() {
  const state = useVersionPolicy();
  if (state === null || state.policy.level !== 'hard') return null;

  const { info, policy } = state;
  return (
    <UpdateRequiredScreen
      title={policy.title ?? FALLBACK_HARD_TITLE}
      message={policy.message ?? FALLBACK_HARD_MESSAGE}
      ctaLabel={policy.cta_label ?? FALLBACK_CTA}
      storeUrl={policy.store_url ?? storeUrlForNative(info.platform)}
      supportUrl={policy.support_url ?? SUPPORT_WHATSAPP_URL}
    />
  );
}

interface UpdateAppBannerHostProps {
  /** Esconde sem desmontar — usado nas rotas imersivas (recarga em andamento,
   *  aspirador), onde um aviso flutuante atrapalharia. */
  hidden?: boolean;
}

export function UpdateAppBannerHost({
  hidden = false
}: UpdateAppBannerHostProps) {
  const state = useVersionPolicy();
  const noticeId = state?.policy.notice_id ?? null;
  const [dismissed, setDismissed] = useState(false);

  if (hidden || dismissed) return null;
  if (state === null || state.policy.level !== 'soft') return null;
  // Dispensa persistida por `notice_id`: subir a versão recomendada no admin
  // muda o id e a faixa reaparece sozinha.
  if (recentlyDismissed(noticeId)) return null;

  const { info, policy } = state;
  return (
    <UpdateAppBanner
      title={policy.title ?? FALLBACK_SOFT_TITLE}
      message={policy.message ?? FALLBACK_SOFT_MESSAGE}
      ctaLabel={policy.cta_label ?? FALLBACK_CTA}
      storeUrl={policy.store_url ?? storeUrlForNative(info.platform)}
      onDismiss={() => {
        markDismissed(noticeId);
        setDismissed(true);
      }}
    />
  );
}
